// The edge: how far the market has moved away from the league's frozen line.
//
// This is the whole thesis in one function. The league freezes its spread when
// the line email arrives; books move until kickoff for an early game or Sunday
// 1:00pm for Sunday/Monday.
// Wherever the two disagree, we are holding a price the books no longer offer.
//
// Everything here works in the HOME team's perspective, which is the only
// representation that survives a favourite flip. `home_spread` is negative
// when the home team is favoured. The legacy `spread` field on each book is
// `-home_spread.abs` — always negative, no team attached — and must not be
// used for arithmetic.
//
// The value of an edge is NOT the size of the gap. It is the chance the final
// margin lands where our frozen number wins and the number the books are
// actually offering does not. Points are a proxy for that, and a poor one: a
// half-point move across 3 is worth several times a two-point move across
// nothing, because NFL margins pile up on a handful of integers. Points are
// kept because they are still the right way to *describe* an edge to a human,
// just not to rank one.
//
// ---------------------------------------------------------------------------
// What this ranks on, and why it is the objective rather than a heuristic
// ---------------------------------------------------------------------------
//
// The pool pays one point per correct pick and NOTHING for a tie (see
// `docs/LEAGUE.md` on scoring, and the standings tiebreakers in
// `docs/FIVE_PICK_RULES_2026.md` — ties are the third of three). Expected pool
// points for a pick is therefore exactly its outright win probability, pushes
// excluded, and choosing the five highest is optimal. The 5-0 bonus does not
// change that: maximising the sum and maximising the product select the same
// five games.
//
// So `computeEdge` ranks on `winProbability`, and reports `lineValueProbability`
// separately. Both matter and they are not the same claim:
//
//   winProbability        which side is more likely to win. What to pick.
//   lineValueProbability  how much of that came from holding a number the
//                         books no longer offer. Why the app exists.
//
// The app's premise is that its edge is TIME, not information — it does not
// claim to predict better than the market. A single blended score let a price
// lean at an unmoved line read as stale-line value, which is exactly that
// claim made by accident. Two fields make it impossible to make silently.

/**
 * Share of NFL games ending on each absolute margin, post extra-point rule
 * (2015 onward). These are published league-wide figures, rounded, and they
 * predate 2023. Recent seasons have drifted: over 2023–2025 (nflverse, 855
 * games) 7 fell to about 8.1% and 1 and 2 rose to about 5% each, while 3 held
 * at 14.9%. A lattice refit on 2015–2025 was tested against this one
 * (2026-09) and did not improve stale-line win probability on held-out
 * seasons, so the table stays as the documented derivation rather than being
 * swapped for numbers that only look fresher.
 *
 * This table is NOT used to value an edge any more. It is retained because it
 * is the empirical input from which `KEY_NUMBER_SHAPE` below was derived, and
 * because that derivation should be reproducible from what is in this file.
 *
 * Its two defects are exactly why it cannot be used directly:
 *
 * 1. It is unconditional. A game lined at 3 lands on 3 far more often than a
 *    game lined at 12 does. Scoring the same 14.8% for "crosses 3" on both is
 *    simply wrong, and it is wrong in a direction — it overrates edges on big
 *    spreads, which is where the old ranking most often disagreed with sense.
 * 2. It is undirected — the frequency of "someone wins by 3", not "the home
 *    team wins by 3" — so it reads about double a real probability.
 */
export const MARGIN_FREQUENCY = {
  0: 0.2, // ties
  1: 3.7,
  2: 3.3,
  3: 14.8,
  4: 5.2,
  5: 2.7,
  6: 5.5,
  7: 9.3,
  8: 3.2,
  9: 2.3,
  10: 5.3,
  11: 2.3,
  12: 1.7,
  13: 3.3,
  14: 4.5,
  15: 1.8,
  16: 2.2,
  17: 3.0,
  18: 1.4,
  19: 1.2,
  20: 1.8,
  21: 1.7,
  22: 1.0,
  23: 0.9,
  24: 1.3,
  25: 0.7,
  26: 0.6,
  27: 0.7,
  28: 0.6,
};

/**
 * Standard deviation of the final margin around the market spread.
 *
 * This is the width of the smooth curve BEFORE the lattice multiplies it, not
 * the spread of real margins, and the two are easy to confuse. Measured
 * directly, final margins sit about 12.7 from the closing line (nflverse,
 * 2015–2025, every era within 12.5–12.8), well under the 13.86 Winston and
 * Stern got off early-eighties seasons. But the lattice piles mass onto 3 and 7
 * near the line, so 13.5 here yields an effective 13.0–13.3 around the line,
 * and a maximum-likelihood fit of sigma jointly with the lattice lands on
 * 13.25–13.5 anyway.
 *
 * Do not "correct" it to 12.7. Re-checked 2026-09 on held-out seasons
 * 2023–2025: narrower sigmas scored WORSE on outright win probability at a
 * stale line (the quantity picks rank on), and swapping sigma between 13 and
 * 14 moved 2025 league pool points by nothing. It is held constant on purpose:
 * across the few points either side of the market line the app reads, the
 * choice rescales every game about equally and reorders nothing.
 */
export const MARGIN_SIGMA = 13.5;

/**
 * How much more (or less) often a margin occurs than a smooth distribution
 * would predict, because football is scored in 3s and 7s rather than in
 * points. This is the scoring lattice, isolated from the spread.
 *
 * Derived, not guessed. The unconditional margin distribution is the mixture
 * of every game's conditional distribution over the league's spread
 * distribution, so dividing the observed unconditional frequencies by the
 * smooth distribution that mixture would have produced without a lattice
 * leaves the lattice by itself:
 *
 *     K(|m|) = MARGIN_FREQUENCY(|m|) / foldedNormal(|m|; mu 2.19, sd 14.68)
 *
 * The mu and sd are the published unconditional figures (PLOS ONE, n = 5412),
 * and they are consistent with the two numbers above by construction:
 * sqrt(13.5^2 + 5.97^2) = 14.76, against a measured 14.68, where 5.97 is the
 * spread's own standard deviation. The three constants agree, which is the
 * main reason to believe any of them.
 *
 * Read the result as football and it is obviously right: 3 happens three times
 * as often as a smooth curve says, 7 twice as often, 14/10/6/17/4 modestly
 * more, and 0, 5, 9, 11 and 12 — the margins that need an awkward combination
 * of scores to reach — considerably less. Ties are almost extinct at 0.08.
 *
 * It stops at 21. Past three touchdowns there are so many ways to arrive at a
 * margin that the lattice washes out, and the source table's own rounding is
 * larger than whatever structure is left; the values it yields out there are
 * noise, so they are not used. See `latticeFactor`.
 */
export const KEY_NUMBER_SHAPE = {
  0: 0.08,
  1: 0.73,
  2: 0.66,
  3: 2.98,
  4: 1.06,
  5: 0.56,
  6: 1.18,
  7: 2.05,
  8: 0.73,
  9: 0.55,
  10: 1.31,
  11: 0.6,
  12: 0.46,
  13: 0.96,
  14: 1.38,
  15: 0.59,
  16: 0.78,
  17: 1.14,
  18: 0.58,
  19: 0.54,
  20: 0.88,
  21: 0.91,
};

/** Past this the scoring lattice is indistinguishable from a smooth curve. */
const LATTICE_MAX = 21;

/** Margins considered when building a distribution. Beyond this is nothing. */
const MARGIN_LIMIT = 80;

/** The margins worth naming in the UI — "crosses 3" is what a bettor reads. */
export const KEY_NUMBERS = [3, 7, 6, 10, 4, 14, 1, 13, 8, 17];

function latticeFactor(margin) {
  const key = Math.abs(margin);
  return key <= LATTICE_MAX ? KEY_NUMBER_SHAPE[key] : 1;
}

const normalDensity = (z) => Math.exp(-0.5 * z * z) / Math.sqrt(2 * Math.PI);

const pmfCache = new Map();

/**
 * Where the smooth curve has to sit for the market's line to be a coin flip.
 *
 * This is the constraint that defines the whole model, and getting it wrong
 * was the subtlest bug in this file. A book's line IS the median of its own
 * distribution — that is what setting a line at even money means. So whatever
 * distribution we build for a game lined at -7, it has to return 50% for a bet
 * at -7, or every number downstream inherits a bias.
 *
 * Centring the smooth curve on -s and then multiplying by the lattice does NOT
 * satisfy that. The lattice is anchored on |margin|, so it is asymmetric about
 * any centre other than zero, and it drags the median with it — a game lined
 * at -10 came out at 45.2% rather than 50%. The centre therefore has to be
 * solved for rather than assumed. Bisection, forty times, memoised per spread.
 */
function solveCentre(spread) {
  const threshold = -spread;

  const coverAt = (centre) => {
    let above = 0;
    let total = 0;
    for (let margin = -MARGIN_LIMIT; margin <= MARGIN_LIMIT; margin += 1) {
      const weight =
        normalDensity((margin - centre) / MARGIN_SIGMA) * latticeFactor(margin);
      total += weight;
      if (margin > threshold) above += weight;
      else if (margin === threshold) above += weight / 2; // a push is half
    }
    return above / total;
  };

  // The true centre is never more than a couple of points off the line; the
  // lattice cannot move a median further than the gap between key numbers.
  let lo = threshold - 4;
  let hi = threshold + 4;
  for (let i = 0; i < 40; i += 1) {
    const mid = (lo + hi) / 2;
    if (coverAt(mid) < 0.5) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/**
 * The distribution of the final margin, given what the market thinks.
 *
 * P(margin = m | market spread s) is proportional to a smooth curve multiplied
 * by the scoring lattice:
 *
 *     weight(m) = normal((m - centre) / sigma) * K(|m|)
 *
 * where `centre` is chosen so that a bet at the market's own number is exactly
 * a coin flip (see `solveCentre`). It lands near -s, because a home-perspective
 * spread of s means the market expects a margin of -s, but not exactly on it.
 *
 * This is the fix for the thing that was actually wrong with this file. The old
 * code charged 14.8% for crossing 3 whether the game was lined at -2.5 or at
 * -13; this charges roughly 17% at a line of 3 and roughly 13% at a line of 10,
 * which is what the published spread-bucket data shows. It also makes the
 * returned number a real, directed probability rather than a ranking score, so
 * a caller can finally show it to a human without lying.
 *
 * Returns a Map from signed margin to probability, summing to 1. Memoised on
 * the spread, because a card asks for this once per book per game.
 *
 * The memo key is rounded, but only far past any resolution a line carries,
 * and that matters more than it looks. Rounding to a TENTH snapped a consensus
 * of -2.9945 onto -3.0 — turning a line no margin can push into one where a
 * margin of 3 is a push, which is a discontinuous change of about half the mass
 * on 3. `solveCentre` then satisfied the coin-flip constraint at -3.0 rather
 * than at the line it was asked about, so a bet at the market's own number came
 * back 54.4% instead of 50%, and every edge measured against a near-integer
 * consensus inherited it. The de-vigged consensus is a weighted mean of ten
 * books; it lands within a hair of an integer routinely.
 */
export function marginPmf(marketSpread) {
  const spread = Math.round(marketSpread * 1e6) / 1e6;
  const cached = pmfCache.get(spread);
  if (cached) return cached;

  const centre = solveCentre(spread);
  const weights = new Map();
  let total = 0;

  for (let margin = -MARGIN_LIMIT; margin <= MARGIN_LIMIT; margin += 1) {
    const weight =
      normalDensity((margin - centre) / MARGIN_SIGMA) * latticeFactor(margin);
    weights.set(margin, weight);
    total += weight;
  }
  for (const [margin, weight] of weights) weights.set(margin, weight / total);

  pmfCache.set(spread, weights);
  return weights;
}

// ---------------------------------------------------------------------------
// The per-book quote-calibrated primitive
// ---------------------------------------------------------------------------
//
// Everything that makes a recommendation is built on this, and it replaced a
// construction that was wrong in two directions at once.
//
// The old path de-vigged every book into `fairSpread`, took a weighted mean of
// those numbers, and scored the frozen line against that single synthetic
// spread. Two things broke.
//
// 1. A push is not half a win. `docs/LEAGUE.md` pays one point per correct
//    pick, and `FIVE_PICK_RULES_2026.md` ranks standings on win points with
//    ties only the THIRD tiebreaker. The old `coverProbability` split a push down the
//    middle, which invents half a win point the pool does not award.
//
// 2. On an integer line a push returns the stake, so a book's de-vigged price
//    is P(win | no push) — not P(win) + half P(push). Calibrating the margin
//    distribution against the latter fits the quote to the wrong quantity.
//
// Together those produced a step function. A weighted mean of nine books all
// posting 3 lands a hair off the integer — 2.9945, say — and a hair is enough:
// our frozen 3 can push where a continuous 2.9945 cannot, so the model booked
// half the mass on 3 as free value. It scored a constant 4.35 points of
// "edge", identically, whether the market had moved 0.02 points or 0.17, and
// that constant outranked a genuine full-point move on every board. It also
// erased real edges in the other direction: a frozen +3.5 against five books
// at +3 wins outright on a 3-point margin, and a consensus of 3.0022 put no
// integer strictly between the two, so the best edge on the 2026 Week 1 board
// scored exactly zero.
//
// The fix is to stop building a line nobody offers. Each book is calibrated
// from its OWN posted spread and price pair, the frozen line is evaluated
// under that book's distribution, and the resulting probabilities are mixed.
// The consensus survives for display only. There is no continuous number left
// to land on the wrong side of an integer, so the discontinuity has nowhere
// to live.

/**
 * A book's de-vigged probability that home wins OUTRIGHT, conditional on the
 * game not pushing against its posted number.
 *
 * This is the quantity a price pair actually expresses. Returns 0.5 when the
 * pair is missing or unusable, which is the neutral reading — "this book
 * thinks its own posted number is fair" — and reproduces the old behaviour on
 * the seeded development data, where prices are absent or symmetric.
 *
 * Null, not 0.5, for a pair that is not a two-way quote at all: the same
 * `MAX_IMPLIED_TOTAL` test `fairSpread` applies, so a -9900/-9900 row is
 * dropped from the mixture rather than entering it as a coin flip.
 */
export function quotedWinProbability(book) {
  if (!isPrice(book?.homePrice) || !isPrice(book?.awayPrice)) return 0.5;

  const home = impliedProbability(book.homePrice);
  const away = impliedProbability(book.awayPrice);
  if (!(home + away > 0)) return 0.5;
  if (home + away > MAX_IMPLIED_TOTAL) return null;

  return home / (home + away);
}

/**
 * Split the margin distribution about a threshold into home win, push and
 * away win. `threshold` is `-spread`: a home-perspective spread `s` covers
 * when `margin + s > 0`, and the integer sitting exactly on it is the push.
 */
function splitAbout(centre, threshold) {
  let win = 0;
  let push = 0;
  let loss = 0;

  for (let margin = -MARGIN_LIMIT; margin <= MARGIN_LIMIT; margin += 1) {
    const weight =
      normalDensity((margin - centre) / MARGIN_SIGMA) * latticeFactor(margin);
    if (margin > threshold) win += weight;
    else if (margin === threshold) push += weight;
    else loss += weight;
  }

  const total = win + push + loss;
  return { win: win / total, push: push / total, loss: loss / total };
}

const centreCache = new Map();

/**
 * Where the smooth curve sits for this book's quote to be satisfied.
 *
 * Solves `win / (win + loss) === probability` at the book's posted number —
 * the conditional-on-no-push reading above. Bisection, memoised.
 *
 * The memo key is the posted spread and the solved probability, NOT the raw
 * prices: two books quoting -108/-102 and -110/100 differ by less than the
 * solver's tolerance and should share an entry. It rounds at 1e-6, and that
 * bound is deliberate — the previous incarnation of this cache rounded to a
 * TENTH, which snapped a near-integer onto the integer and silently changed a
 * line no margin can push into one where a margin pushes. Do not widen it.
 */
function solveCentreForQuote(spread, probability) {
  const key = `${Math.round(spread * 1e6) / 1e6}:${Math.round(probability * 1e6) / 1e6}`;
  const cached = centreCache.get(key);
  if (cached !== undefined) return cached;

  const threshold = -spread;
  let lo = threshold - 14;
  let hi = threshold + 14;
  for (let i = 0; i < 60; i += 1) {
    const mid = (lo + hi) / 2;
    const { win, loss } = splitAbout(mid, threshold);
    if (win / (win + loss) < probability) lo = mid;
    else hi = mid;
  }

  const centre = (lo + hi) / 2;
  centreCache.set(key, centre);
  return centre;
}

/**
 * One book's opinion, evaluated at our frozen line.
 *
 * Returns the outright win/push/loss probabilities for the HOME side at
 * `frozen`, plus `lineValue` — how much outright win probability the frozen
 * number holds over the number this book is actually offering.
 *
 * `lineValue` is the honest expression of the app's thesis: not "we predict
 * better than the book" but "we hold a number the book no longer offers".
 * Both terms are evaluated under the SAME calibrated distribution, so the
 * model's calibration error very largely cancels out of the difference —
 * which is why line value is the more trustworthy of the two numbers and the
 * one the strength bands read.
 *
 * The comparison point is the book's POSTED number, and it has to be. The
 * de-vigged number is continuous, so it can sit a hair off an integer exactly
 * as the old weighted-mean consensus did — and a hair is all the artifact ever
 * needed. Measured against fair lines, a market sitting flat on 3 with nine
 * books posting 3 and none having moved reported five points of line value.
 * That is the same bug wearing a different hat, and it was caught here by the
 * posted-movement tally reading 0 of 9 beside it.
 *
 * The price information is NOT discarded by this. It sets the calibration —
 * `solveCentreForQuote` shifts the whole distribution by the price skew — so
 * it reaches `win`, `push` and `loss`, which is where it belongs. A book that
 * posts our own number while pricing itself past it is telling us about the
 * game, not offering us a better bet, and line value is only ever about the
 * second. That separation is the reason these are two fields and not one.
 */
export function bookOutlook(book, frozen) {
  const posted = book?.homeSpread;
  if (typeof posted !== "number" || Number.isNaN(posted)) return null;
  if (typeof frozen !== "number" || Number.isNaN(frozen)) return null;

  const probability = quotedWinProbability(book);
  if (probability == null) return null;

  const centre = solveCentreForQuote(posted, probability);
  const atFrozen = splitAbout(centre, -frozen);
  const atPosted = splitAbout(centre, -posted);

  return {
    win: atFrozen.win,
    push: atFrozen.push,
    loss: atFrozen.loss,
    // Each side's line value is computed from ITS OWN win probability. It is
    // NOT the other side's negated, and that is the one place this model does
    // have to branch on side.
    //
    // `win + push + loss === 1`, so negating home's difference leaves the push
    // term behind: frozen -1.5 against a book posting -1 gains home nothing
    // (both win on margins of 2 or more) while gaining away the margin of 1,
    // which our +1.5 wins outright and their +1 only pushes. Deriving one from
    // the other reported that as zero, and the strip drew the books offering
    // us a real margin in the same grey as the books offering us nothing.
    //
    // The old code was right to say it must not branch on side, and that rule
    // died with the half-push: when a push counted as half a win the two
    // differences really were mirror images.
    homeLineValue: atFrozen.win - atPosted.win,
    awayLineValue: atFrozen.loss - atPosted.loss,
  };
}

/**
 * The weighted mixture of every book's outlook, in percentage points.
 *
 * Mixing PROBABILITIES rather than lines is the other half of the fix. Cover
 * probability is a near-step function of a book's number, so the probability
 * of the average line is not the average of the probabilities — plugging a
 * mean into a step is precisely the error that produced the constant 4.35.
 * Averaging the outputs instead lets genuine book disagreement do the
 * smoothing, with no uncertainty floor and no voting threshold.
 *
 * Two identical quotes therefore contribute two identical probabilities
 * rather than dragging a mean, which is the mildest possible treatment of a
 * correlated feed. It is not a substitute for the weighting audit.
 */
export function marketOutlook(books, frozen) {
  let win = 0;
  let push = 0;
  let loss = 0;
  let homeLineValue = 0;
  let awayLineValue = 0;
  let weight = 0;
  let counted = 0;

  for (const book of books || []) {
    const outlook = bookOutlook(book, frozen);
    if (!outlook) continue;

    const w = BOOK_WEIGHTS[book.bookmaker] ?? DEFAULT_BOOK_WEIGHT;
    win += w * outlook.win;
    push += w * outlook.push;
    loss += w * outlook.loss;
    homeLineValue += w * outlook.homeLineValue;
    awayLineValue += w * outlook.awayLineValue;
    weight += w;
    counted += 1;
  }

  if (!weight) return null;
  return {
    win: (win / weight) * 100,
    push: (push / weight) * 100,
    loss: (loss / weight) * 100,
    homeLineValue: (homeLineValue / weight) * 100,
    awayLineValue: (awayLineValue / weight) * 100,
    books: counted,
  };
}

/**
 * The margins our frozen number wins and the market's number does not.
 *
 * A home-perspective spread `s` covers when `margin + s > 0`, so the margin
 * threshold for a line is simply `-s`. The gap between the two thresholds is
 * the whole edge, and — this is the part worth keeping — it does not need to
 * branch on which side the value is. Betting home at the low threshold and
 * betting away at the high one produce the same interval; only the direction
 * of the bet differs, and that is already in `edge.side`.
 *
 * Integer endpoints count HALF, because a whole-number line pushes rather than
 * losing. Frozen -3 against market -4 wins nothing outright: a 3-point margin
 * is a push for us and a loss for them, and a 4-point margin is a win for us
 * and a push for them. Two half-units, not one whole one.
 */
export function marginsGained(frozen, market) {
  if (frozen == null || market == null) return [];

  const lo = Math.min(-frozen, -market);
  const hi = Math.max(-frozen, -market);
  if (lo === hi) return [];

  const gained = [];
  for (let margin = Math.ceil(lo); margin <= Math.floor(hi); margin += 1) {
    const isEndpoint = margin === lo || margin === hi;
    gained.push({ margin, weight: isEndpoint ? 0.5 : 1 });
  }
  return gained;
}

/**
 * Does a book's POSTED number change our side's OUTRIGHT win set?
 *
 * A home bet at `s` wins on every integer margin `m > -s`, so two lines win on
 * the same margins exactly when `floor(-s)` agrees; an away bet wins on
 * `m < -s`, which is `ceil`. Frozen -3 against a posted -3.5 is the same win
 * set (both win on 4+): the half point only turns our tie into their loss, and
 * the pool pays nothing for a tie. Returns the margins our number wins and the
 * book's does not — empty when the book has not moved past us.
 */
export function marginsWonOver(frozen, posted, side) {
  if (typeof frozen !== "number" || typeof posted !== "number") return [];
  if (Number.isNaN(frozen) || Number.isNaN(posted)) return [];
  const gained = [];
  if (side === "away") {
    // Away wins on m < -s: ours reaches up to ceil(-frozen) - 1, theirs to
    // ceil(-posted) - 1.
    for (let m = Math.ceil(-posted); m < Math.ceil(-frozen); m += 1) gained.push(m);
  } else {
    for (let m = Math.floor(-frozen) + 1; m <= Math.floor(-posted); m += 1) gained.push(m);
  }
  return gained;
}

/**
 * The key numbers (as |margin|) our number wins and some SCORED book's posted
 * one does not — and none at all unless the line value itself is worth
 * printing. One stray book is not a cross: frozen -4 against eight books at
 * -3.5 and one at -7.5 has a negative line value, and "crosses 7" beside it
 * claimed a gain the mixture says we do not have. A book `bookOutlook` refuses
 * (a pair that is not a two-way quote) is not in the mixture, so it cannot
 * name a key number either.
 */
function keyNumbersCrossed(books, frozen, side, lineValue) {
  const crossed = new Set();
  if (!(lineValue >= LINE_VALUE_MIN)) return [];
  for (const book of books || []) {
    if (!bookOutlook(book, frozen)) continue;
    for (const margin of marginsWonOver(frozen, book?.homeSpread, side)) {
      const key = Math.abs(margin);
      if (KEY_NUMBERS.includes(key)) crossed.add(key);
    }
  }
  return [...crossed];
}

/**
 * Line value as the card prints it: "+5.1%", "-1.2%", and "0.0%" for anything
 * that rounds to zero. Rounding is done before the sign is chosen, so a
 * -3.5e-16 of float noise cannot print "-0.0" (the old `+ 0` caught only an
 * exact signed zero).
 */
export function formatLineValue(value) {
  if (value == null || Number.isNaN(value)) return "—";
  if (Math.abs(value) < LINE_VALUE_MIN) return "0.0%";
  return `${value > 0 ? "+" : "-"}${Math.abs(value).toFixed(1)}%`;
}

/** Line value below this prints as 0.0% and names no key number (percentage points). */
export const LINE_VALUE_MIN = 0.05;

/** Below this a tie is not worth words (percentage points). */
export const TIE_MIN = 0.05;

/** "8.8% tie", or "" when a tie is impossible or rounds away. The pool's word is tie (W/L/T). */
export function tieLabel(pushProbability) {
  if (pushProbability == null || Number.isNaN(pushProbability)) return "";
  if (pushProbability < TIE_MIN) return "";
  return `${pushProbability.toFixed(1)}% tie`;
}

/** Probabilities equal to twelve places are equal; below this is float noise. */
const EPSILON = 1e-9;

const isPrice = (value) => typeof value === "number" && Math.abs(value) >= 100;

/** American odds to the probability they imply, vig included. */
function impliedProbability(price) {
  return price > 0 ? 100 / (price + 100) : -price / (-price + 100);
}

/**
 * The most a real book's two prices may imply in total before the pair stops
 * being a two-way quote at all. Mirrors `MarketLine::MAX_IMPLIED_TOTAL`, which
 * is where such a row is actually excluded; this is the second line of defence,
 * and it closes a genuine hole in the de-vig below rather than merely repeating
 * the server's filter.
 *
 * The de-vig normalises by `home / (home + away)`, which throws the sum away —
 * so a pair at -9900/-9900, implying 99% and 99%, reads as *perfectly
 * balanced* and its posted number passes through untouched. That is how a
 * 23-point quote reached a double-weighted consensus. Every real book in the
 * basket sits between 1.00 and 1.07.
 */
export const MAX_IMPLIED_TOTAL = 1.15;

/**
 * The line a book is really offering, once its prices are taken into account.
 *
 * A book posting home -3 at -120 and away +3 at -100 is not offering -3. It is
 * offering something closer to -3.2, and charging for the difference in cents
 * because it cannot move the line in less than a half point. Averaging the
 * posted numbers and throwing the prices away loses that, and a few tenths of
 * a point is exactly the resolution at which a key-number crossing flips.
 *
 * The answer is read off the same per-book primitive that drives win
 * probability: calibrate this book's distribution from its own posted number
 * and de-vigged price (`solveCentreForQuote`), then find the even-money line
 * whose distribution is that one (`evenMoneyCentre`). The fair line is the
 * number at which a book holding this opinion would price both sides alike.
 *
 * This replaced a linear extrapolation — the price excess divided by a slope
 * measured AT the posted number — which was right only while the fair line
 * stayed within a point of it. An exchange quoting an alternate strike, CLE +1
 * at +123/-130 while every other book posted +2.5, has a shallow slope at +1
 * (margins of 1 and 2 are rare), so the straight line ran across the key
 * number 3 and landed at +4.10. The exact reading is about +2.8: a distribution
 * calibrated on that quote is the one an even-money book posts between +2.5
 * and +3. That is one conversion, with no clamp and no special case, and a far
 * strike needs neither to find its way back to the market.
 *
 * The old slope also averaged both sides of the posted number, so near a key
 * number it was wrong in whichever direction the price actually pointed: home
 * +2.5 at -120/-100 read +2.09, when the line it is worth sits down in the dead
 * zone between 1 and 2 — the margins it gives up are the rare ones. Normal
 * quotes move by a tenth or two from that reading; that is the correction.
 *
 * There is no cap on the price excess any more. `MAX_PRICE_EXCESS` existed to
 * stop the linear extrapolation running off the board; an exact conversion
 * cannot, and a pair that is not a two-way quote at all is refused by
 * `MAX_IMPLIED_TOTAL` above rather than trimmed.
 *
 * Falls back to the posted number when either price is missing or the pair is
 * balanced, so the seeded development data reads exactly as posted.
 */
export function fairSpread(book) {
  const spread = book?.homeSpread;
  if (typeof spread !== "number" || Number.isNaN(spread)) return null;
  if (!isPrice(book.homePrice) || !isPrice(book.awayPrice)) return spread;

  const home = impliedProbability(book.homePrice);
  const away = impliedProbability(book.awayPrice);
  if (!(home + away > 0)) return spread;
  // Not a market. null, not the posted number: a row this book cannot be
  // held to is not evidence, and letting the spread through would keep it in
  // the consensus with only its price correction suppressed.
  if (home + away > MAX_IMPLIED_TOTAL) return null;

  const probability = home / (home + away);
  if (probability === 0.5) return spread;

  return lineForCentre(solveCentreForQuote(spread, probability), spread);
}

/**
 * Where the smooth curve sits for a book posting `line` at even money.
 *
 * Exact on the half-point grid, where it is `solveCentreForQuote(line, 0.5)`.
 * Between grid points it is interpolated, and has to be: margins are integers,
 * so every line strictly between 2 and 3 wins on the same margins and an
 * even-money book cannot be told apart anywhere in that open interval. A fair
 * line of 2.8 therefore MEANS "eight tenths of the way from the +2.5 opinion to
 * the +3 one", and this is the one place that says so — `fairSpread` inverts
 * it and `evenMoneyProbability` reads it forward, so the two are the same map.
 *
 * Decreasing in `line`: a home team getting more points is expected to lose by
 * more. The steps are wildly uneven — +2.5 to +3 moves the centre about 1.6
 * points, +2 to +2.5 about 0.35 — which is the key number showing through.
 */
function evenMoneyCentre(line) {
  const below = Math.floor(line * 2) / 2;
  const cBelow = solveCentreForQuote(below, 0.5);
  if (below === line) return cBelow;
  const cAbove = solveCentreForQuote(below + 0.5, 0.5);
  return cBelow + (cAbove - cBelow) * (line - below) * 2;
}

/** Half-point steps to search either side of a posted number; 40 points. */
const FAIR_LINE_SEARCH = 80;

/** The line whose even-money distribution is centred at `centre`. */
function lineForCentre(centre, posted) {
  // Walk the half-point grid from the posted number to the step that brackets
  // the centre, then interpolate inside it exactly as `evenMoneyCentre` does.
  let below = Math.round(posted * 2) / 2;
  for (let i = 0; i < FAIR_LINE_SEARCH && solveCentreForQuote(below, 0.5) < centre; i += 1) {
    below -= 0.5;
  }
  for (let i = 0; i < FAIR_LINE_SEARCH && solveCentreForQuote(below + 0.5, 0.5) >= centre; i += 1) {
    below += 0.5;
  }

  const cBelow = solveCentreForQuote(below, 0.5);
  const cAbove = solveCentreForQuote(below + 0.5, 0.5);
  if (!(cBelow > cAbove)) return below;
  return below + (0.5 * (cBelow - centre)) / (cBelow - cAbove);
}

/**
 * The probability an even-money book posting `line` gives home at `atLine`,
 * pushes excluded.
 *
 * The forward direction of `fairSpread`, on the same interpolation: feed a
 * book's fair line back in at its posted number and its own de-vigged
 * probability comes out. Exported so the round trip can be tested as a round
 * trip rather than as a magic number.
 */
export function evenMoneyProbability(line, atLine) {
  const { win, loss } = splitAbout(evenMoneyCentre(line), -atLine);
  return win / (win + loss);
}

/**
 * Books whose number is worth more than the others'.
 *
 * Pinnacle prices to the lowest margin in the business and moves on money
 * rather than on the other books; the no-vig and prediction exchanges have no
 * vig at all, so their price *is* a probability. The retail books largely
 * follow. This is a deliberately mild thumb on the scale — enough to let a
 * sharp number pull the consensus a tenth or two, not enough to let one feed
 * hijack it.
 *
 * The tiers are not all one step. A flat 2 for everything sharp made the
 * "sharp" half of the basket six books deep and put Pinnacle at 2 of 17 units,
 * about 12% — so the tier that is supposed to anchor the consensus was
 * anchored not by the one book with real limits but by four thin venues that
 * happen to charge no vig. Pinnacle at 4 puts it near a quarter of the weight,
 * which is roughly what it is worth: it is the book the others move to, and
 * when it disagrees with the exchanges it is usually the exchanges' liquidity
 * talking rather than information.
 *
 * Polymarket sits at 1.5 — still an exchange, still badged sharp, but its feed
 * is the one that keeps arriving unreadable (`MarketLine::MAX_IMPLIED_TOTAL`
 * rejects the majority of its rows, reported per book on /api/v1/health), and
 * the rows that survive are the widest in the basket. The weight is about how
 * much a *surviving* row should move the number, which is less than a quote
 * from a venue whose feed is not intermittently broken. It stays above 1 on
 * purpose: the badge and the weight are the same list (`isSharpBook`), and a
 * no-vig exchange is what it is even on a bad week.
 *
 * The keys are Odds API bookmaker keys, matching `market_lines.bookmaker` and
 * `Odds::Client::SHARP_BOOKMAKERS` — `mapStoredGame` passes that string through
 * unchanged. Keep the two lists in step.
 *
 * `betonlineag` is gone on purpose, and its absence is the whole reason this
 * comment exists. LowVig.ag is BetOnline's reduced-juice skin — the two posted
 * an identical spread in 33 of 33 events — so weighting both at 2 counted one
 * operator's single opinion FOUR times, outweighing Pinnacle. A duplicate feed
 * is worse here than a missing one: it does not merely add nothing, it drags
 * the median toward whichever number that operator happens to be showing.
 * Before adding a key, check it is not a skin of one already listed.
 */
export const BOOK_WEIGHTS = {
  pinnacle: 4,
  lowvig: 2,
  novig: 2,
  prophetx: 2,
  kalshi: 2,
  polymarket: 1.5,
};

const DEFAULT_BOOK_WEIGHT = 1;

/**
 * The books whose number is an estimate of the truth rather than partly a
 * record of where the public money is.
 *
 * Derived from the weights rather than listed again: a book that counts double
 * in the consensus and a book badged "sharp" in the UI must be the same book,
 * and two hand-kept lists would drift the first time one is edited.
 */
export function isSharpBook(bookmaker) {
  return (BOOK_WEIGHTS[String(bookmaker || "").toLowerCase()] ?? DEFAULT_BOOK_WEIGHT) > DEFAULT_BOOK_WEIGHT;
}

/**
 * The single book carrying the most weight in the consensus.
 *
 * Derived from the weights for the same reason `isSharpBook` is: the book the
 * strip marks as the anchor and the book that actually moves the number most
 * must be one and the same, and a hardcoded "pinnacle" would keep claiming the
 * title after a retiering moved it.
 *
 * Strict maximum on purpose — a tie means no book is the anchor, and marking
 * two of them would say the opposite of what the mark is for.
 */
export function isAnchorBook(bookmaker, table = BOOK_WEIGHTS) {
  const weight = table[String(bookmaker || "").toLowerCase()];
  if (weight == null) return false;

  const weights = Object.values(table);
  const top = Math.max(...weights);
  return weight === top && weights.filter(w => w === top).length === 1;
}

/**
 * Where the market really is, in home-perspective points.
 *
 * De-vigs each book against its own prices, then takes a weighted mean with
 * the sharp books counting double. Returns null if no book priced the game.
 */
export function marketConsensus(books) {
  let weighted = 0;
  let weight = 0;

  for (const book of books || []) {
    const fair = fairSpread(book);
    if (fair == null || Number.isNaN(fair)) continue;

    const w = BOOK_WEIGHTS[book.bookmaker] ?? DEFAULT_BOOK_WEIGHT;
    weighted += w * fair;
    weight += w;
  }

  return weight ? weighted / weight : null;
}

/**
 * Compare the frozen line against the market.
 *
 * Ranked on `winProbability` — the outright chance THIS pick wins, pushes
 * excluded. That is expected pool points directly: one point per correct
 * pick, nothing for a tie. It replaced a symmetric "probability gained"
 * against a synthetic consensus, which measured something the pool does not
 * pay for and which carried the integer artifact described above.
 *
 * The two numbers are deliberately separate and mean different things:
 *
 *   `winProbability`   which side is more likely to win, all-in. The ranking.
 *   `lineValueProbability`  how much of that came from holding a number the
 *                      books no longer offer. The app's actual thesis, and
 *                      the quantity the strength bands read.
 *
 * Blending them into one score is what made a pure price lean at an unmoved
 * line read as stale-line value — the app quietly claiming to out-predict the
 * market, which is the one thing it does not do.
 *
 * Expect an integer frozen line to rank BELOW a half-point one, all else
 * equal. Sitting on 3 is not a bonus, it is an 8.8% chance of scoring
 * nothing, and the old model had the sign of that backwards.
 *
 * `market` and `points` survive for display only. Nothing here ranks, sides
 * or scores on them.
 */
export function computeEdge(game) {
  const frozen = game.fivePicksSpread;
  const market = marketConsensus(game.spreads);
  const outlook = marketOutlook(game.spreads, frozen);

  if (frozen == null || outlook == null) {
    return {
      frozen,
      market,
      points: null,
      winProbability: null,
      pushProbability: null,
      lossProbability: null,
      lineValueProbability: null,
      margins: [],
      keyNumbers: [],
      side: null,
      measurable: false,
    };
  }

  // The side with the larger OUTRIGHT win probability. Not the side the
  // consensus leans to: on a game sitting flat on an integer those can
  // disagree, and the win probability is the one the pool pays on.
  const side = outlook.win >= outlook.loss ? "home" : "away";

  const delta = market == null ? null : market - frozen;
  const margins = market == null ? [] : marginsGained(frozen, market);
  // `+ 0` normalises a signed zero, which is not `0` to `Object.is`.
  const lineValue = (side === "home" ? outlook.homeLineValue : outlook.awayLineValue) + 0;
  const crossed = keyNumbersCrossed(game.spreads, frozen, side, lineValue);

  return {
    frozen,
    market,
    points: delta == null ? null : Math.abs(delta),
    winProbability: side === "home" ? outlook.win : outlook.loss,
    pushProbability: outlook.push,
    lossProbability: side === "home" ? outlook.loss : outlook.win,
    // The recommended side's OWN line value, not the other side's negated —
    // see the push note in `bookOutlook`. Positive when the frozen number is
    // the better one, negative when we hold the worse side of a market that
    // has moved against us.
    lineValueProbability: lineValue,
    comparedBooks: outlook.books,
    margins,
    // Which key numbers our number wins outright and at least one scored
    // book's POSTED number does not — the same win sets line value is scored
    // on — and only when that line value is positive, so "crosses 3" can never
    // print beside a zero or negative value. It used to read the
    // consensus gap (`margins`), and a frozen -3 against a de-vigged -3.002
    // "crossed 3" with nothing gained. Best first, by this game's own odds.
    keyNumbers: crossed.sort((a, b) => {
      const pmf = marginPmf(market ?? frozen);
      return (pmf.get(b) ?? 0) + (pmf.get(-b) ?? 0) - (pmf.get(a) ?? 0) - (pmf.get(-a) ?? 0);
    }),
    side,
    measurable: true,
  };
}

/** The book offering the most extreme number in the direction of the edge. */
export function bestBook(game, edge) {
  // Deliberately the posted number, not the de-vigged one: this answers "which
  // book shows the best version of our bet", and a book displays what it posts.
  const books = (game.spreads || []).filter(
    (b) => typeof b.homeSpread === "number" && !Number.isNaN(b.homeSpread)
  );
  if (!books.length || !edge.side) return null;

  // Value on home means we want the book whose home number is the LEAST
  // favourable to home — that is the one closest to (or beyond) our frozen
  // price, i.e. the market's own best remaining version of our bet.
  return books.reduce((best, book) =>
    edge.side === "home"
      ? book.homeSpread > best.homeSpread ? book : best
      : book.homeSpread < best.homeSpread ? book : best
  );
}

/**
 * How much outright win probability our frozen line holds over one book's
 * actual number, in percentage points, signed to the recommended side.
 *
 * This is `bookOutlook`'s `lineValue` — the frozen line and the book's posted
 * line scored under that book's OWN calibrated distribution. It used to score
 * both under one distribution derived from the consensus, which is what tied
 * every per-book number to the synthetic spread and let the integer artifact
 * reach the strip and the tally as well as the card.
 *
 * `rankBooks` orders and shades its rows on this and `bookTally` counts on it,
 * so the dot, the colour and the "N for / N against" sentence above them can
 * never come from different arithmetic. Ordering the panel by `fairSpread`
 * would break that: the panel makes a recommendation, and a recommendation
 * has to be scored the way the recommendation is scored.
 */
export function gainOverBook(book, frozen, direction = 1) {
  const outlook = bookOutlook(book, frozen);
  if (!outlook) return null;
  // `direction` names the side, it does not negate a single number — see the
  // push note in `bookOutlook`.
  return (direction < 0 ? outlook.awayLineValue : outlook.homeLineValue) * 100;
}

/**
 * How the books split on our number: how many de-vigged past it, how many
 * trail it, and how many were comparable at all.
 *
 * This is the "N favour this side" count, and it reads each book's DE-VIGGED
 * number — the same quantity that places and shades that book's dot on the
 * strip, so the sentence and the picture above it cannot disagree.
 *
 * It is descriptive, not the recommendation. "Favour" says where the market
 * really is; what our line is actually worth is `lineValueProbability`, scored
 * on posted numbers because probability value is quantised to whole margins.
 * A book can favour us by a tenth of a point and still offer us nothing, and
 * both of those are true at once.
 *
 * Ties count as neither, and a tie here means the de-vigged numbers agree.
 */
export function bookTally(game, edge) {
  const empty = { for: 0, against: 0, compared: 0 };
  if (!edge?.measurable || !edge.side) return empty;

  const direction = edge.side === "away" ? -1 : 1;

  return (game.spreads || []).reduce((tally, book) => {
    const fair = fairSpread(book);
    if (fair == null || Number.isNaN(fair)) return tally;

    const favour = (edge.frozen - fair) * direction;
    tally.compared += 1;
    if (favour > EPSILON) tally.for += 1;
    else if (favour < -EPSILON) tally.against += 1;
    return tally;
  }, { ...empty });
}

/**
 * How many books have moved their POSTED number off the frozen line far enough
 * to change our side's OUTRIGHT win set.
 *
 * The other half of the old "N books past frozen". This one answers "has the
 * market physically moved off our number", which is a different question from
 * whether a book's price implies it should have, and deserves its own words.
 *
 * Only a move that changes what we win counts (`marginsWonOver`). Frozen -3
 * against a book now posting -3.5 used to count as moved, but both win on
 * exactly the same margins — the half point only turns our tie into their loss,
 * which pays nothing in this pool. So this is no longer the exact complement of
 * `frozenAvailability`, which reads the literal posted number: such a book has
 * stopped posting ours without having moved off our win set.
 */
export function postedMovementTally(game, edge) {
  const empty = { beyond: 0, compared: 0 };
  if (!edge?.measurable || !edge.side) return empty;

  return (game.spreads || []).reduce((tally, book) => {
    const posted = book?.homeSpread;
    if (typeof posted !== "number" || Number.isNaN(posted)) return tally;

    tally.compared += 1;
    if (marginsWonOver(edge.frozen, posted, edge.side).length) tally.beyond += 1;
    return tally;
  }, { ...empty });
}

/**
 * Where the sharp books sit against the retail books, on the same de-vigged
 * numbers the dots are plotted at.
 *
 * The two counts above say how far the market has ALREADY moved. This says
 * whether it is likely to keep moving, and it is the only forward-looking
 * thing the panel can honestly show. Retail books follow sharp books and not
 * the reverse — a sharp number is an estimate of the truth, a retail number is
 * partly a record of where the public money is (see `BOOK_WEIGHTS`) — so a gap
 * between the two cohorts is a gap that closes in the sharps' direction.
 *
 * `lead` is signed to the recommended side, the same convention `bookTally`
 * uses: positive means the sharps sit FURTHER in our favour than retail does,
 * so the drift that built this edge has not finished; negative means retail
 * has run past the sharps, which is the case worth knowing about, because it
 * says the movement flattering our number is public money rather than sharp
 * money.
 *
 * Means, not medians, of a handful of numbers — but gated by `SHARP_SPLIT_MIN`
 * below rather than trusted blindly, because at three books a side one outlier
 * is the whole signal.
 *
 * Note the sharp mean is close to `marketConsensus` by construction: the
 * consensus already weights these same books 1.5x to 4x. The number that
 * carries new information is the RETAIL one, and the caller should say so.
 */
export function sharpSplit(game, edge) {
  const empty = { sharp: null, retail: null, lead: null, sharpCount: 0, retailCount: 0, usable: false };
  if (!edge?.measurable || !edge.side) return empty;

  const sharp = [];
  const retail = [];

  for (const book of game.spreads || []) {
    const fair = fairSpread(book);
    if (fair == null || Number.isNaN(fair)) continue;
    (isSharpBook(book.bookmaker) ? sharp : retail).push(fair);
  }

  const mean = (values) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : null);
  const sharpMean = mean(sharp);
  const retailMean = mean(retail);

  // Home-perspective, so which way counts as "in our favour" flips with the
  // side: a home edge wants the smaller number, an away edge the larger. Same
  // flip `bookTally` makes, written the other way round because this compares
  // two cohorts rather than a book against the frozen line.
  const toward = edge.side === "away" ? 1 : -1;
  const lead =
    sharpMean == null || retailMean == null ? null : (sharpMean - retailMean) * toward;

  return {
    sharp: sharpMean,
    retail: retailMean,
    lead,
    sharpCount: sharp.length,
    retailCount: retail.length,
    // Both cohorts have to be big enough that one book is not the entire
    // signal. The gap itself is NOT gated here — the values are always worth
    // showing; only the sentence that reads something into them is.
    usable: sharp.length >= SHARP_SPLIT_MIN && retail.length >= SHARP_SPLIT_MIN,
  };
}

/**
 * Which books, if any, will still give you the frozen number.
 *
 * Close to the complement of `postedMovementTally`, but stated as the thing a
 * reader can act on rather than as a count of movement. "7 of 8 have moved the posted number
 * past ours" and "only FanDuel still posts it" are the same fact; only the
 * second one tells you where to look, and only the second one can be checked
 * against the Posts column sitting directly underneath it.
 *
 * Posted numbers, deliberately, the same call `bestBook` makes: this answers
 * "can I still see our number on a screen", and a book displays what it posts.
 * A book posting BETTER than our number counts as still offering it — it is
 * not the frozen line, it beats it.
 *
 * `only` is set just when exactly one book is left, because that is the case
 * where naming it is useful; past one, the count says it better than a list
 * would. `closest` is the fallback for when none are left — `bestBook`'s
 * answer to "what is the best remaining version of this bet".
 */
export function frozenAvailability(game, edge) {
  const empty = { count: 0, compared: 0, only: null, closest: null };
  if (!edge?.measurable || !edge.side) return empty;

  const direction = edge.side === "away" ? -1 : 1;
  const holding = (game.spreads || []).filter((book) => {
    const posted = book?.homeSpread;
    if (typeof posted !== "number" || Number.isNaN(posted)) return false;
    // The literal posted number, at ours or better. Not the complement of
    // `postedMovementTally` any more — see its note.
    return (posted - edge.frozen) * direction >= -EPSILON;
  });

  return {
    count: holding.length,
    compared: (game.spreads || []).filter(
      (book) => typeof book?.homeSpread === "number" && !Number.isNaN(book.homeSpread)
    ).length,
    only: holding.length === 1 ? holding[0] : null,
    closest: holding.length ? null : bestBook(game, edge),
  };
}

/** Fewer books than this on either side and the mean is one book's opinion. */
export const SHARP_SPLIT_MIN = 3;

/** Below this the two cohorts agree, and a gap this small is rounding. */
export const SHARP_SPLIT_EPSILON = 0.25;

/**
 * Format a home-perspective number the way a bettor reads it: "SF -2.5".
 *
 * Rounded, like `formatSigned`: a posted book number is already a half point,
 * but `marketConsensus` returns a de-vigged weighted mean, so an unrounded
 * value here renders as "CIN -3.794058091836" across the card.
 */
export function formatLine(homeSpread, homeAbbr, awayAbbr, digits = 1) {
  if (homeSpread == null || Number.isNaN(homeSpread)) return "—";
  const rounded = Number(homeSpread.toFixed(digits));
  if (rounded === 0) return "PK";
  return rounded < 0
    ? `${homeAbbr} ${rounded}`
    : `${awayAbbr} -${rounded}`;
}

/** Signed number only, for dense grids that name the team elsewhere. */
export function formatSigned(value, digits = 1) {
  if (value == null || Number.isNaN(value)) return "—";
  const rounded = Number(value.toFixed(digits));
  if (rounded === 0) return "PK";
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
}

/**
 * Thresholds for how much a gained probability is worth showing, in percentage
 * points of real, directed probability.
 *
 * These are half what they were, and deliberately: the old score was
 * undirected — "someone wins by 3" — so it read about double a probability.
 * A half point across 3 scores about 6.5 here where it scored 14.8 before.
 * Nothing about the ranking changed; the units became honest.
 */
/**
 * Line-value bands, in percentage points of outright win probability.
 *
 * These read `lineValueProbability` — the STRENGTH of the stale-line edge, not
 * confidence in it. A one-point market move backed by two books is strong
 * value and weak confidence; those are different facts and must not share a
 * scale. Confidence belongs with source agreement, coverage and freshness
 * (`comparedBooks`, the consensus dispersion, `market_frozen_at`).
 *
 * The thresholds are unchanged through the rewrite, which is not an oversight.
 * The old score counted the integer margins between the frozen line and the
 * consensus, at half weight on a pushing endpoint; this counts the margins
 * between the frozen line and what the books actually post, at full weight.
 * On the games the old score handled at all the two agree closely — a genuine
 * full point across 6 reads 3.48 either way — so the scale a reader has
 * learned still means what it meant. What changed is that games the old score
 * flattened to zero now register, and the fictitious 4.35 no longer does.
 */
export const EDGE_BANDS = { slight: 1.25, solid: 3, strong: 6 };

/**
 * The ladder, in order, with the words the UI prints. One list, because there
 * were two and neither read as a ladder.
 *
 * The card ran Thin / Slim / Solid / Strong and `/history` ran trace / faint /
 * warm / strong. "Thin" and "slim" are synonyms, so nobody could tell which
 * was the stronger — the question that produced this list was literally "so
 * even thin is better than slim?" — and "warm" is not a strength word at all.
 * Two independent vocabularies for one scale is also how they drift.
 *
 * Ascending, and each word has to be obviously above the one below it:
 * Flat (the market has not moved off our number) → Slight → Solid → Strong.
 */
export const EDGE_BAND_ORDER = [
  { key: "flat", label: "Flat", min: 0 },
  { key: "slight", label: "Slight", min: EDGE_BANDS.slight },
  { key: "solid", label: "Solid", min: EDGE_BANDS.solid },
  { key: "strong", label: "Strong", min: EDGE_BANDS.strong },
];

/** The band a line value falls in. Never below `flat`. */
export function edgeBand(lineValue) {
  const value = lineValue || 0;
  let band = EDGE_BAND_ORDER[0];
  for (const candidate of EDGE_BAND_ORDER) {
    if (value >= candidate.min) band = candidate;
  }
  return band.key;
}
